import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { styles, cx } from "../styles";
import { useTheme } from "../providers/ThemeProvider";

export interface TabItem {
  id: string;
  label: string;
}

interface SortableTabBarProps {
  tabs: TabItem[];
  activeTabId: string;
  /** Tabs that can't be dragged or closed (built-in). */
  fixedTabIds: string[];
  onTabClick: (id: string) => void;
  onReorder: (activeId: string, overId: string) => void;
  onAddTab?: () => void;
  onRemoveTab?: (id: string) => void;
  /** Icon per tab id (desktop pill + mobile nav). */
  getIcon: (id: string) => React.ReactNode;
  /** Custom label resolver (e.g. i18n). Defaults to tab.label. */
  labelFor?: (tab: TabItem) => string;
  /** Extra controls rendered at the right of the desktop bar (e.g. settings). */
  rightSlot?: React.ReactNode;
}

// ─── One draggable pill (module scope → stable identity for framer layout) ────

interface TabPillProps {
  tab: TabItem;
  isActive: boolean;
  isFixed: boolean;
  animateLayout: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  onRemove: () => void;
}

const TabPill: React.FC<TabPillProps> = ({
  tab,
  isActive,
  isFixed,
  animateLayout,
  label,
  icon,
  onClick,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: isFixed });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isActive ? 20 : 10,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx("relative flex items-center", isActive && "z-20")}
      {...attributes}
      {...listeners}
    >
      <button
        onClick={onClick}
        className={cx(
          styles.sortableTabs.tab.base,
          isActive
            ? styles.sortableTabs.tab.active
            : styles.sortableTabs.tab.inactive,
          isDragging && styles.sortableTabs.tab.dragging,
        )}
      >
        {isActive && (
          <motion.span
            layoutId="tabPillDesktop"
            className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25 ring-1 ring-blue-400/30 pointer-events-none"
            transition={
              animateLayout
                ? { type: "spring", stiffness: 500, damping: 40 }
                : { duration: 0 }
            }
          />
        )}
        <span
          className={cx(
            styles.sortableTabs.tabContent.container,
            "relative z-10",
          )}
        >
          <span className="flex-shrink-0">{icon}</span>
          {label}
          {!isFixed && (
            <span
              className={styles.sortableTabs.tabContent.closeButton.container}
            >
              <button
                className={styles.sortableTabs.tabContent.closeButton.button}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                title="Close tab"
                tabIndex={-1}
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className={styles.sortableTabs.tabContent.closeButton.icon}
                >
                  <path
                    d="M7 7l10 10M7 17L17 7"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          )}
        </span>
      </button>
    </div>
  );
};

const AddButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    title="Add tab"
    className={styles.sortableTabs.addButton.button}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={styles.sortableTabs.addButton.icon}
    >
      <path
        d="M12 6v12M6 12h12"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  </button>
);

/**
 * Shared tab bar for the machine and line views: a modern animated pill bar on
 * desktop (with drag-to-reorder) and a fixed bottom navigation on mobile. One
 * component so both views look and behave identically.
 */
const SortableTabBar: React.FC<SortableTabBarProps> = ({
  tabs,
  activeTabId,
  fixedTabIds,
  onTabClick,
  onReorder,
  onAddTab,
  onRemoveTab,
  getIcon,
  labelFor,
  rightSlot,
}) => {
  const { currentTheme } = useTheme();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  // Gate pill slide until the first click (no replay while state settles on load).
  const [interacted, setInteracted] = useState(false);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  const click = (id: string) => {
    if (id !== activeTabId) setInteracted(true);
    onTabClick(id);
  };

  const label = (tab: TabItem) => (labelFor ? labelFor(tab) : tab.label);

  return (
    <>
      {/* Desktop: pill bar with drag-and-drop reordering */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="hidden sm:block">
          <SortableContext
            items={tabs.map((t) => t.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div
              className={styles.sortableTabs.container}
              style={{
                backgroundColor: currentTheme.isDark
                  ? "var(--bg-secondary)"
                  : undefined,
              }}
            >
              {tabs.map((tab) => (
                <TabPill
                  key={tab.id}
                  tab={tab}
                  isActive={activeTabId === tab.id}
                  isFixed={fixedTabIds.includes(tab.id)}
                  animateLayout={interacted}
                  label={label(tab)}
                  icon={getIcon(tab.id)}
                  onClick={() => click(tab.id)}
                  onRemove={() => onRemoveTab?.(tab.id)}
                />
              ))}
              {onAddTab && <AddButton onClick={onAddTab} />}
              {rightSlot && <div className="ml-auto pl-2">{rightSlot}</div>}
            </div>
          </SortableContext>
        </div>
      </DndContext>

      {/* Mobile: fixed bottom navigation */}
      <nav
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 flex items-stretch gap-1 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] overflow-x-auto no-scrollbar"
        style={{
          backgroundColor: currentTheme.isDark
            ? "var(--bg-secondary)"
            : "rgba(255,255,255,0.95)",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTabId === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => click(tab.id)}
              className={cx(
                "flex flex-col items-center justify-center gap-1 min-w-[64px] flex-1 py-1.5 rounded-xl transition-colors duration-200",
                isActive
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-400 dark:text-gray-400",
              )}
            >
              <span className="relative flex items-center justify-center w-10 h-8 rounded-full">
                {isActive && (
                  <motion.span
                    layoutId="tabPillMobile"
                    className="absolute inset-0 rounded-full bg-blue-100 dark:bg-blue-500/20 pointer-events-none"
                    transition={
                      interacted
                        ? { type: "spring", stiffness: 500, damping: 40 }
                        : { duration: 0 }
                    }
                  />
                )}
                <span className="relative z-10">{getIcon(tab.id)}</span>
              </span>
              <span className="text-[10px] font-medium leading-none max-w-[68px] truncate">
                {label(tab)}
              </span>
            </button>
          );
        })}
        {onAddTab && (
          <button
            onClick={onAddTab}
            title="Add tab"
            className="flex flex-col items-center justify-center gap-1 min-w-[52px] py-1.5 text-gray-400 dark:text-gray-400"
          >
            <span className="flex items-center justify-center w-10 h-8 rounded-full bg-blue-600 text-white">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
                <path
                  d="M12 6v12M6 12h12"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="text-[10px] font-medium leading-none">Add</span>
          </button>
        )}
      </nav>
    </>
  );
};

export default SortableTabBar;
